import {
  getHealth,
  getMe,
  getSetup,
  languages,
  MenuIcon,
  MoonIcon,
  Popover,
  SIGNED_OUT,
  SignedOut,
  signOut,
  SunIcon,
  updateMe,
  type Health,
  type User,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';

import { Bookings } from '../pages/bookings/Bookings';
import { CityDays } from '../pages/city/CityDays';
import { Journal } from '../pages/journal/Journal';
import { RoadSights } from '../pages/sights/RoadSights';
import { Calendar } from '../pages/calendar/Calendar';
import { DataSources } from '../pages/data/DataSources';
import { Security } from '../pages/account/Security';
import { MyPlaces } from '../pages/places/MyPlaces';
import { MapAdd } from '../pages/mapadd/MapAdd';
import { WayDraw } from '../pages/waydraw/WayDraw';
import { Record } from '../pages/record/Record';
import { PlanActual } from '../pages/actual/PlanActual';
import { Assistant } from '../pages/assistant/Assistant';
import { Authorize } from '../pages/assistant/Authorize';
import { Users } from '../pages/admin/Users';
import { Join } from '../pages/sign-in/Join';
import { Stats } from '../pages/stats/Stats';
import { Lists } from '../pages/lists/Lists';
import { Forgot } from '../pages/sign-in/Forgot';
import { Reset } from '../pages/sign-in/Reset';
import { Compare } from '../pages/compare/Compare';
import { Clocks } from '../pages/clocks/Clocks';
import { Countries } from '../pages/countries/Countries';
import { Docs } from '../pages/docs/Docs';
import { Group } from '../pages/group/Group';
import { History } from '../pages/history/History';
import { Sharing } from '../pages/sharing/Sharing';
import { Invitation } from '../pages/invitation/Invitation';
import { Kit } from '../pages/kit/Kit';
import { forgetOffline } from './offline';
import { ArchivedBanner, ChangesOnly, TripAccess, useReadOnly } from './readOnly';
import { Planner } from '../pages/plan/Planner';
import { SignIn } from '../pages/sign-in/SignIn';
import { applyTheme, savedTheme } from './theme';
import { Today } from '../pages/today/Today';
import { Trips } from '../pages/trips/Trips';
import { TripNav } from './TripNav';
import { LiveTrip } from './live';
import { LiveToasts } from './LiveToasts';
import { TripSettings } from '../pages/settings/TripSettings';
import { TripView } from '../pages/view/TripView';
import { Emissions } from '../pages/emissions/Emissions';
import { MailImport } from '../pages/mail/MailImport';
import { FlightWatch } from '../pages/flights/FlightWatch';
import { OfflineMaps } from '../pages/offline/OfflineMaps';
import { ExportDialog } from '../components/trip/ExportDialog';
import { ImportWindow } from '../components/trip/ImportDialog';

type ServerState = { kind: 'checking' } | { kind: 'ok'; schema: number } | { kind: 'down' };

/** Who is signed in: not known yet, no one (and whether the server has any account), someone. */
type Me = { kind: 'checking' } | { kind: 'out'; first: boolean } | { kind: 'in'; user: User };

interface Props {
  /** How the server is asked; tests pass their own. */
  loadHealth?: () => Promise<Health>;
}

/** The shell every screen lives in: the bar, the language switch, the server's state. */
export function App({ loadHealth = getHealth }: Props) {
  const { t, i18n } = useTranslation();
  const [server, setServer] = useState<ServerState>({ kind: 'checking' });
  const [me, setMe] = useState<Me>({ kind: 'checking' });
  const [menu, setMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [theme, setTheme] = useState(savedTheme);
  const { pathname } = useLocation();
  // An invitation and a read-only link open without signing in.
  const open = /^\/(invite|share|reset|join)\/|^\/forgot$/.test(pathname);
  // A trip's own pages carry its bar; a viewer's route view and the kit do not.
  // A trip's page, a stop's days within it too; not the view one shares.
  const trip =
    /^\/trips\/([^/]+)(?:\/(?!view$)[^/]*|\/stops\/[^/]+(?:\/add|\/way|\/record)?|\/bookings\/mail)?$/.exec(
      pathname,
    )?.[1];

  useEffect(() => {
    loadHealth().then(
      (health) => {
        setServer({ kind: 'ok', schema: health.schema });
      },
      () => {
        setServer({ kind: 'down' });
      },
    );
  }, [loadHealth]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    const out = () => {
      getSetup().then(
        (first) => {
          setMe({ kind: 'out', first });
        },
        () => {
          setMe({ kind: 'out', first: false });
        },
      );
    };
    getMe().then(
      (user) => {
        setMe({ kind: 'in', user });
      },
      (error: unknown) => {
        if (error instanceof SignedOut) out();
        else setMe({ kind: 'out', first: false });
      },
    );
    window.addEventListener(SIGNED_OUT, out);
    return () => {
      window.removeEventListener(SIGNED_OUT, out);
    };
  }, []);

  // The account's language is the one shown; choosing another changes the account.
  const spoken = me.kind === 'in' ? me.user.language : null;
  useEffect(() => {
    if (spoken !== null) void i18n.changeLanguage(spoken);
  }, [spoken, i18n]);

  const signedIn = (user: User) => {
    setMe({ kind: 'in', user });
  };
  const choose = (language: string) => {
    if (me.kind === 'in') {
      setMe({ kind: 'in', user: { ...me.user, language } });
      updateMe(me.user.name, language).then(
        () => undefined,
        () => undefined,
      );
    } else {
      void i18n.changeLanguage(language);
    }
  };

  const languageButtons = (
    <nav className="languages" aria-label={t('language.label')}>
      {languages.map((language) => (
        <button
          key={language.code}
          type="button"
          lang={language.code}
          aria-pressed={i18n.language === language.code}
          onClick={() => {
            choose(language.code);
          }}
        >
          {language.name}
        </button>
      ))}
    </nav>
  );
  // A sun in the dark, a moon in the light: pressed, the other.
  const themeButton = (
    <button
      type="button"
      className="bar-icon theme-toggle"
      aria-pressed={theme === 'dark'}
      aria-label={t('theme.dark')}
      title={t('theme.dark')}
      onClick={() => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setTheme(next);
        applyTheme(next, true);
      }}
    >
      {theme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
    </button>
  );
  const close = () => {
    setMenu(false);
  };
  const signOutButton = (
    <button
      type="button"
      role="menuitem"
      className="data-menu-item"
      onClick={() => {
        close();
        signOut().then(
          () => {
            setMe({ kind: 'out', first: false });
            void forgetOffline();
          },
          () => undefined,
        );
      }}
    >
      {t('account.signOut')}
    </button>
  );
  // The trip's own: who sees it, what changed, its settings.
  const tripLinks =
    trip !== undefined &&
    (
      [
        ['sharing', 'tabs.share'],
        ['history', 'tabs.history'],
        ['settings', 'tabs.settings'],
        ['offline', 'tabs.offline'],
        ['actual', 'tabs.actual'],
        ['lists', 'tabs.lists'],
      ] as const
    ).map(([path, key]) => (
      <Link
        key={path}
        role="menuitem"
        className="data-menu-item"
        to={`/trips/${trip}/${path}`}
        aria-current={pathname === `/trips/${trip}/${path}` ? 'page' : undefined}
        onClick={close}
      >
        {t(key)}
      </Link>
    ));
  // The trip taken elsewhere: its book, its map, its tables, its calendar.
  const exportButton = trip !== undefined && (
    <button
      type="button"
      role="menuitem"
      className="data-menu-item"
      onClick={() => {
        close();
        setExporting(true);
      }}
    >
      {t('tabs.export')}
    </button>
  );
  // Places from a list or a file taken into the trip; none into one read only.
  const importButton = trip !== undefined && (
    <ImportItem
      onPick={() => {
        close();
        setImporting(true);
      }}
    />
  );
  // Everyone's own: their places apart from any trip.
  const placesLink = me.kind === 'in' && (
    <Link
      role="menuitem"
      className="data-menu-item"
      to="/places"
      aria-current={pathname === '/places' ? 'page' : undefined}
      onClick={close}
    >
      {t('myPlaces.menu')}
    </Link>
  );
  // Everyone's own: their trips added up.
  const statsLink = me.kind === 'in' && (
    <Link
      role="menuitem"
      className="data-menu-item"
      to="/stats"
      aria-current={pathname === '/stats' ? 'page' : undefined}
      onClick={close}
    >
      {t('stats.open')}
    </Link>
  );
  // Everyone's own: the account and its security.
  const securityLink = me.kind === 'in' && (
    <Link
      role="menuitem"
      className="data-menu-item"
      to="/account"
      aria-current={pathname === '/account' ? 'page' : undefined}
      onClick={close}
    >
      {t('security.menu')}
    </Link>
  );
  // Everyone's own: their AI assistant and keys.
  const assistantLink = me.kind === 'in' && (
    <Link
      role="menuitem"
      className="data-menu-item"
      to="/account/assistant"
      aria-current={pathname === '/account/assistant' ? 'page' : undefined}
      onClick={close}
    >
      {t('assistant.menu')}
    </Link>
  );
  // Whoever keeps the server keeps its accounts.
  const usersLink = me.kind === 'in' && me.user.owner && (
    <Link
      role="menuitem"
      className="data-menu-item"
      to="/admin/users"
      aria-current={pathname === '/admin/users' ? 'page' : undefined}
      onClick={close}
    >
      {t('people.menu')}
    </Link>
  );
  // Whoever runs the server keeps its reference data.
  const dataLink = me.kind === 'in' && me.user.owner && (
    <Link role="menuitem" className="data-menu-item" to="/admin/data" onClick={close}>
      {t('account.dataSources')}
    </Link>
  );

  const status =
    server.kind === 'ok'
      ? t('status.ok', { schema: server.schema })
      : t(server.kind === 'down' ? 'status.down' : 'status.checking');

  return (
    <LiveTrip
      trip={me.kind === 'in' && trip !== undefined ? trip : null}
      me={me.kind === 'in' ? me.user.id : null}
    >
      <TripAccess trip={me.kind === 'in' && trip !== undefined ? trip : null}>
        <a className="skip" href="#main">
          {t('skip')}
        </a>
        <header className="bar">
          <Link className="brand" to="/" aria-label={t('brand')} title={t('brand')}>
            <img className="brand-mark" src="/icon-192.png" alt="" width={28} height={28} />
            <span className="brand-name">{t('brand')}</span>
          </Link>
          {me.kind === 'in' && trip !== undefined && <TripNav trip={trip} />}
          {/* Said on the bar only when it is wrong: else it is at the menu's foot. */}
          {server.kind === 'down' && (
            <p className="status status-down" role="alert">
              {status}
            </p>
          )}
          <div className="bar-end">
            {themeButton}
            <Popover
              open={menu}
              onOpenChange={setMenu}
              align="end"
              label={t('tabs.menu')}
              trigger={(props) => (
                <button
                  type="button"
                  className="bar-icon bar-menu"
                  aria-label={
                    me.kind === 'in'
                      ? t('account.menuOf', { name: me.user.name })
                      : t('tabs.menu')
                  }
                  title={t('tabs.menu')}
                  {...props}
                >
                  <MenuIcon size={20} />
                </button>
              )}
            >
              <div className="account-menu">
                {me.kind === 'in' && <p className="account-menu-name">{me.user.name}</p>}
                {languageButtons}
                {me.kind === 'in' && (
                  <div className="data-menu" role="menu" aria-label={t('tabs.menu')}>
                    {tripLinks}
                    {exportButton}
                    {importButton}
                    {placesLink}
                    {statsLink}
                    {securityLink}
                    {assistantLink}
                    {usersLink}
                    {dataLink}
                    {signOutButton}
                  </div>
                )}
                <p className={`status status-${server.kind}`} role="status">
                  {status}
                </p>
              </div>
            </Popover>
          </div>
        </header>
        <main id="main" tabIndex={-1}>
          {me.kind === 'checking' && <p className="page quiet">{t('trips.loading')}</p>}
          {open && me.kind !== 'checking' && (
            <Routes>
              <Route
                path="/invite/:token"
                element={
                  <Invitation user={me.kind === 'in' ? me.user : null} onSignedIn={signedIn} />
                }
              />
              <Route path="/share/:token" element={<TripView shared />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset/:token" element={<Reset />} />
              <Route path="/join/:token" element={<Join onSignedIn={signedIn} />} />
            </Routes>
          )}
          {!open && me.kind === 'out' && <SignIn first={me.first} onSignedIn={signedIn} />}
          {!open && me.kind === 'in' && trip !== undefined && <ArchivedBanner />}
          {!open && me.kind === 'in' && (
            <Routes>
              <Route path="/trips/:id" element={<Planner />} />
              <Route
                path="/trips/:id/variants"
                element={<Planner key="variants" opens={['variants']} />}
              />
              <Route path="/trips/:id/bookings" element={<Bookings />} />
              <Route path="/trips/:id/calendar" element={<Calendar />} />
              <Route path="/trips/:id/stops/:stop" element={<CityDays />} />
              <Route path="/trips/:id/journal" element={<Journal />} />
              <Route path="/trips/:id/stops/:stop/sights" element={<RoadSights />} />
              <Route
                path="/trips/:id/stops/:stop/add"
                element={
                  <ChangesOnly>
                    <MapAdd />
                  </ChangesOnly>
                }
              />
              <Route
                path="/trips/:id/stops/:stop/way"
                element={
                  <ChangesOnly>
                    <WayDraw />
                  </ChangesOnly>
                }
              />
              <Route
                path="/trips/:id/stops/:stop/record"
                element={
                  <ChangesOnly>
                    <Record />
                  </ChangesOnly>
                }
              />
              <Route path="/trips/:id/search" element={<ToPlanSearch />} />
              <Route path="/trips/:id/routes" element={<Compare />} />
              <Route path="/trips/:id/countries" element={<Countries />} />
              <Route path="/trips/:id/clocks" element={<Clocks />} />
              <Route path="/trips/:id/emissions" element={<Emissions />} />
              <Route
                path="/trips/:id/bookings/mail"
                element={
                  <ChangesOnly>
                    <MailImport />
                  </ChangesOnly>
                }
              />
              <Route path="/trips/:id/flights" element={<FlightWatch />} />
              <Route path="/trips/:id/offline" element={<OfflineMaps />} />
              <Route path="/trips/:id/actual" element={<PlanActual />} />
              <Route path="/trips/:id/lists" element={<Lists />} />
              <Route path="/trips/:id/settings" element={<TripSettings />} />
              <Route path="/kit" element={<Kit />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/admin/data" element={<DataSources />} />
              <Route path="/admin/users" element={<Users />} />
              <Route path="/account" element={<Security />} />
              <Route path="/account/assistant" element={<Assistant />} />
              <Route path="/oauth/authorize" element={<Authorize />} />
              <Route path="/places" element={<MyPlaces />} />
              <Route path="/trips/:id/view" element={<TripView />} />
              <Route path="/trips/:id/group" element={<Group />} />
              <Route path="/trips/:id/sharing" element={<Sharing />} />
              <Route path="/trips/:id/history" element={<History />} />
              <Route path="/trips/:id/today" element={<Today />} />
              <Route path="/trips/:id/docs" element={<Docs />} />
              <Route path="*" element={<Trips />} />
            </Routes>
          )}
          {trip !== undefined && importing && (
            <ImportWindow
              trip={trip}
              onClose={() => {
                setImporting(false);
              }}
            />
          )}
          {/* Made afresh each time it opens: its choices start from the language then. */}
          {trip !== undefined && exporting && (
            <ExportDialog
              trip={trip}
              open
              onClose={() => {
                setExporting(false);
              }}
            />
          )}
        </main>
        {me.kind === 'in' && trip !== undefined && <LiveToasts trip={trip} />}
      </TripAccess>
    </LiveTrip>
  );
}

/** The menu's way to take places into the trip: not there for one read only. */
function ImportItem({ onPick }: { onPick: () => void }) {
  const { t } = useTranslation();
  if (useReadOnly()) return null;
  return (
    <button type="button" role="menuitem" className="data-menu-item" onClick={onPick}>
      {t('tabs.import')}
    </button>
  );
}

/** The old search page's address: the search is on the plan's map now. */
function ToPlanSearch() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  return <Navigate to={`/trips/${id}?${params.toString()}`} replace />;
}
